import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getData, getcountries, getsubdomains } from '../../../services/management/transactionStatus/transactionStatusServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import cardIcon from "../../../../src/assets/img/hdfc_card.webp";
import paypalIcon  from "../../../../src/assets/img/paypal_icon.webp";
import razorpayIcon  from "../../../../src/assets/img/razorpay_icon.webp";
import checkoutIcon  from "../../../../src/assets/img/checkout_icon.webp";

const TransactionStatus = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeoutRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
   // Filter States
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [subDomainFilter, setSubDomainFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [dropdownData, setDropdownData] = useState({
          countries: [],
          subdomains: []
    });
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    
    // Debounce Effect
    useEffect(() => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

      debounceTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(searchText);
        setCurrentPage(1); // reset page only when actual search triggered
      }, 600); // 500ms debounce delay

      return () => clearTimeout(debounceTimeoutRef.current);
    }, [searchText]);
  
   // Fetch data list
       const fetchData = useCallback(async () => {
           setLoading(true);
           try {
             const filters = {};
             if (subDomainFilter) filters.domain = subDomainFilter;
             if (countryFilter) filters.country = countryFilter;
       
             const params = {
               page: currentPage,
               limit: rowsPerPage,
               search: debouncedSearch,
               filters,
               startDate: startDate || null,
               endDate: endDate || null,
             };
       
             const response = await getData(params);
             // ✅ Console log the full API response
             console.log('Fetched Data:', response);
             setDataList(response.rows);
             setTotalRows(response.count);
           } catch (error) {
             console.error('Failed to fetch data:', error);
           } finally {
             setLoading(false);
           }
       }, [currentPage, rowsPerPage, debouncedSearch, subDomainFilter, countryFilter, startDate, endDate]);
         

    // Fetch all dropdown data
    const fetchDropdownData = useCallback(async () => {
      try {
        const [country, subdomains] = await Promise.all([
          getcountries(),
          getsubdomains()            
        ]);
    
        setDropdownData({
          countries: country,
          subdomains: subdomains
        });
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    }, []);
  
    useEffect(() => {
      fetchDropdownData();
    }, [fetchDropdownData]);
          
    useEffect(() => {
      fetchData();
    }, [fetchData]);

    //based country code append country name
  const getCountryName = (code) => {
  const country = dropdownData.countries.find(c => c.code === code);
  return country ? country.name : code;
  };
  

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };

  //payment icon
  const getPaymentIcon = (type) => {
    switch (type.toLowerCase()) {
      case "card":
        return cardIcon;
      case "paypal":
        return paypalIcon
      case "razorpay":
        return razorpayIcon
      case "initiated":
        return checkoutIcon
      default:
        return null;
    }
  };
  
  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '60px',
    },
    {
      name: 'Date',
      selector: (row) => new Date(row.createdAt).toLocaleDateString('en-CA'),
      sortable: true,
    },
    {
      name: 'Sub Domain',
      selector: (row) => row.domain,
      sortable: true,
    },
    {
      name: 'Title',
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: 'First Name',
      selector: (row) => row.first_name,
      sortable: true,
    },
    {
      name: 'Last Name',
      selector: (row) => row.last_name,
      sortable: true,
    },
    {
      name: 'Country',
      selector: (row) => getCountryName(row.country),
      sortable: true,
    },
    {
        name: 'Amount',
        selector: (row) => row.amount,
        sortable: true,
        cell: (row) => {
        let currencySymbol = '';
      
          switch (row.currency_type) {
          case 1: // USD
            currencySymbol = '$';
            break;
          case 2: // EUR
            currencySymbol = '€';
            break;
          case 3: // INR
            currencySymbol = '₹';
            break;
          default:
            currencySymbol = row.currency || ''; // Fallback
        }
      
          return (
            <span>
              <b className='fs-3'>{currencySymbol} {row.amount}</b>
            </span>
          );
        },
      },      
      {
        name: "Payment Mode",
        cell: (row) => {
          const icon = getPaymentIcon(row.payment_type);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {icon ? (
                <img
                  src={icon}
                  width={70}
                  alt={row.payment_type}
                  style={{
                    borderRadius: "6px",
                    boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                  }}
                />
              ) : (
                <span style={{ fontWeight: "500" }}>{row.payment_type}</span>
              )}
            </div>
          );
        },
        sortable: true,
      },
      {
          name: 'Status',
          selector: (row) => row.status,
          sortable: true,
          cell: (row) => {
            let bgColor = "";
            let textLabel = "";

          switch (row.status) {
          case "CHARGED":
            bgColor = "bg-success";
            textLabel = "Success";
            break;

          case "AUTHORIZATION_FAILED":
          case "AUTHENTICATION_FAILED":
            bgColor = "bg-danger";
            textLabel = "Failed";
            break;

          case "PENDING_VBV":
            bgColor = "bg-warning text-dark";
            textLabel = "Pending";
            break;

          case "JUSPAY_DECLINED":
            bgColor = "bg-secondary";
            textLabel = "Declined";
            break;

          default:
            bgColor = "bg-secondary";
            textLabel = row.status;
        }

        return (
          <span className={`rounded text-center w-100 py-1 text-white ${bgColor}`}>
            {textLabel}
          </span>
        );
      },
      }    
      
  ];

  // Function to render expandable row details
  const ExpandableRow = ({ data }) => {
    return (
      <div className="p-3 bg-light border">
        <table className="table table-bordered">
          <tbody>
            <tr>
              <td><b>Order ID:</b></td>
              <td>{data.selfregistration_id }</td>
            </tr>
            <tr>
              <td><b>Trans ID:</b></td>
              <td>{data.trnx}</td>
            </tr> 
            <tr>
              <td><b>Email ID:</b></td>
              <td>{data.email}</td>
            </tr>
            <tr>
              <td><b>Contact Number:</b></td>
              <td>{data.contactnumber}</td>
            </tr>
            <tr>
              <td><b>Address:</b></td>
              <td>{data.postal_address}</td>
            </tr>
            <tr>
              <td><b>created Date:</b></td>
              <td>{data.createdAt}</td>
            </tr>
            <tr>
              <td><b>Upadted Date:</b></td>
              <td>{data.updatedAt}</td>
            </tr>
             <tr>
              <td><b>Status:</b></td>
              <td>{data.status}</td>
            </tr>
            {data.payment_type?.toLowerCase() !== "paypal" && (
            <tr>
              <td colSpan="2" className="text-center">
                <a
                  href={`https://www.${data.domain}.scientexconference.com/registrationstatus?param=${data.selfregistration_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm mt-2"
                  onClick={(e) => {
                    e.preventDefault(); // prevent default navigation
                    const url = `https://www.${data.domain}.scientexconference.com/registrationstatus?param=${data.selfregistration_id}`;
                    window.open(url, "_blank"); // open in new tab
                    // setTimeout(() => window.location.reload(), 800); // reload current page after short delay
                  }}
                >
                  🔄 Refresh Status
                </a>
                
              </td>
            </tr>
            )}     
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => { setSubDomainFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Subdomains</option>
                {dropdownData.subdomains.map((subdomains) => (
                  <option key={subdomains.sub_domain} value={subdomains.sub_domain}>{subdomains.sub_domain}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Countries</span>
              <select className="form-control" value={countryFilter} onChange={(e) => { setCountryFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Countries</option>
                {dropdownData.countries.map((country) => (
                  <option key={country.code} value={country.code}>{country.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="col-md-2">
              <span>End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} />
            </div>
          </div>
        </div>
        {loading ? (
                        <div
                          className="d-flex justify-content-center align-items-center w-100"
                          style={{ minHeight: "300px" }}
                        >
                          <Spinner/>
                        </div>
                                ) :(
                <div>
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-8">
              <p className='fs-4'>Revenue Management / Transactions Status</p>
            </div>
          </div>

          <DataTable
                columns={columns}
                data={dataList}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 30, 50, 100, 500]}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                responsive
                striped
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
                expandableRows
                expandableRowsComponent={ExpandableRow}  // ✅ Adding expandable row
            />
        </div>
        <Footer />
        </div>
        )};

      </div>
    </div>
  );
};

export default TransactionStatus;
